import { useEffect, useMemo, useState } from 'react';

import { Dialog, DialogContent } from '@web/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ContractData, contractDataSchema } from '@shared/types';
import { trpc, trpcQueryUtils } from '@web/lib/trpc';
import { useForm } from 'react-hook-form';
import { EditContractForm } from './edit-contract-form';
import { toast } from 'sonner';

export default function ContractEditor({
  contractId,
  onClose,
}: {
  contractId: string;
  onClose?: () => void;
}) {
  const { data: allProceduresData } = trpc.procedure.listAll.useQuery();

  const { data: allProductsData } = trpc.product.listAll.useQuery();

  const { data: contractData } = trpc.contracts.getContract.useQuery({
    contractId: contractId,
  });

  const { data: doctorData } = trpc.doctor.getDoctorsForContractForm.useQuery();

  const { mutateAsync: updateDraftAsync, error } =
    trpc.contracts.updateContract.useMutation();

  useEffect(() => {
    if (error) {
      toast.error(`Error updating contract`, {
        description: 'Check the input fields for errors',
      });
    }
  }, [error]);

  const { mutateAsync: submitContractByIdAsync } =
    trpc.contracts.submitContractById.useMutation();

  const [isOpen, setIsOpen] = useState(true);

  const allProcedures = useMemo(
    () =>
      allProceduresData?.map((p) => ({
        id: p.id,
        name: p.displayName,
        price: p.price,
      })) ?? [],
    [allProceduresData],
  );

  const allProducts = useMemo(
    () =>
      allProductsData?.map((p) => ({
        id: p.id,
        name: p.displayName,
        price: p.salesPrice ?? 0,
      })) ?? [],
    [allProductsData],
  );

  const allDoctors = useMemo(
    () => [
      {
        id: 'null',
        name: 'Select Doctor',
        brandName: '',
      },
      ...(doctorData?.map((d) => ({
        id: d.id,
        name: d.displayName ? d.displayName : 'Unknown',
        brandName: d.brand?.name ? d.brand.name : 'Unknown',
      })) ?? []),
    ],
    [doctorData],
  );

  const contractDefaultValues = useMemo(
    () => ({
      id: contractData?.id ?? '',
      name: contractData?.name ?? '',
      firstName: contractData?.firstName ?? '',
      lastName: contractData?.lastName ?? '',
      email: contractData?.email ?? '',
      phoneNumber: contractData?.phoneNumber ?? '',
      surgeonId: contractData?.surgeonId ?? '',
      procedures:
        contractData?.procedures.map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
        })) ?? [],
      products:
        contractData?.products.map((p) => ({
          id: p.id,
          name: p.name,
          price: p.price,
        })) ?? [],
      discount: contractData?.discount,
      subTotal: contractData?.subTotal,
    }),
    [contractData],
  );

  const form = useForm<ContractData>({
    resolver: zodResolver(contractDataSchema),
    defaultValues: contractDefaultValues,
  });

  const handleSubmit = async () => {
    await submitContractByIdAsync({ contractId });
    setIsOpen(false);
    onClose?.();
  };

  const handleUpdate = async () => {
    await updateDraftAsync({
      contractId,
      data: form.getValues(),
    });
  };

  useEffect(() => {
    form.reset(contractDefaultValues);
  }, [contractDefaultValues, form]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) onClose?.();
    void trpcQueryUtils.contracts.getContract.refetch();
  };

  if (!contractData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="block max-h-[90vh] overflow-y-auto rounded-lg shadow-lg border-gray-200 p-0 md:p-0 lg:p-0 w-[90vw] md:w-[80vw] lg:w-[60vw] max-w-none"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogPrimitive.Close className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600 opacity-70 transition-opacity hover:opacity-100 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
        <EditContractForm
          form={form}
          onUpdate={handleUpdate}
          onSubmit={handleSubmit}
          allProcedures={allProcedures}
          allProducts={allProducts}
          allDoctors={allDoctors}
          editable={contractData.status === 'draft'}
        />
      </DialogContent>
    </Dialog>
  );
}
